import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../../context/Auth.js";

const AddExpenseModal = ({ expenseTypes, modal, setModal, getExpenseList }) => {
  const { requestWithAuth } = useAuth();
  const [formData, setFormData] = useState({
    date: '',
    expense_type: '',
    new_expense_type: '',
    amount: '',
    description: '',
    payment_method: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // HANDLE INPUT CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // VALIDATE FORM
  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.expense_type) {
      newErrors.expense_type = 'Expense type is required';
    }

    if (formData.expense_type === 'Other' && !formData.new_expense_type?.trim()) {
      newErrors.new_expense_type = 'New expense type name is required';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid amount is required';
    }

    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // RESET FORM
  const resetForm = () => {
    setFormData({
      date: '',
      expense_type: '',
      new_expense_type: '',
      amount: '',
      description: '',
      payment_method: ''
    });
    setErrors({});
  };

  // CLOSE MODAL
  const closeModal = () => {
    resetForm();
    setModal(false);
  };

  // RECORD EXPENSE
  const recordExpense = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the data payload
      const payload = {
        date_recorded: formData.date,
        expense_type: formData.expense_type,
        new_expense_type: formData.expense_type === 'Other' ? formData.new_expense_type.trim() : null,
        amount_spent: parseFloat(formData.amount),
        description: formData.description.trim(),
        payment_method: formData.payment_method || null
      };

      await requestWithAuth('/finance/expense/', 'POST',JSON.stringify(payload), {
        'Content-Type': 'application/json'
      } );

      // Success handling
      await Swal.fire({
        title: 'Success!',
        text: 'Expense has been recorded successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc2626'
      }).then(()=>{
        window.location.reload();
      });

      closeModal();
      await getExpenseList(); // Refresh the expense list

    } catch (error) {
      console.error('Error recording expense:', error);
      
      let errorMessage = 'An error occurred while recording the expense';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Record Expense</h3>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <form onSubmit={recordExpense} className="space-y-4">
            {/* Date Field */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.date ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
            </div>

            {/* Expense Type Field */}
            <div>
              <label htmlFor="expense_type" className="block text-sm font-medium text-gray-700 mb-1">
                Expense Type *
              </label>
              <select
                id="expense_type"
                name="expense_type"
                value={formData.expense_type}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.expense_type ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              >
                <option value="">Select Expense Type</option>
                {expenseTypes?.map((expenseType) => (
                  <option value={expenseType.id} key={expenseType.id}>
                    {expenseType.name}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
              {errors.expense_type && <p className="text-red-500 text-xs mt-1">{errors.expense_type}</p>}
            </div>

            {/* New Expense Type Field (conditional) */}
            {formData.expense_type === 'Other' && (
              <div>
                <label htmlFor="new_expense_type" className="block text-sm font-medium text-gray-700 mb-1">
                  New Expense Type *
                </label>
                <input
                  type="text"
                  id="new_expense_type"
                  name="new_expense_type"
                  value={formData.new_expense_type}
                  onChange={handleChange}
                  placeholder="Enter new expense type name"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    errors.new_expense_type ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.new_expense_type && (
                  <p className="text-red-500 text-xs mt-1">{errors.new_expense_type}</p>
                )}
              </div>
            )}

            {/* Amount Field */}
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                Amount Spent (GHS) *
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.amount ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>

            {/* Payment Method Field */}
            <div>
              <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                id="payment_method"
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={isSubmitting}
              >
                <option value="">Select Payment Method</option>
                <option value="Cash">Cash</option>
                <option value="Credit Card">Credit Card</option>
                
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Mobile Money">Mobile Money</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Description Field */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Enter expense description..."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <ThreeDots
                      height="20"
                      width="20"
                      radius="9"
                      color="#ffffff"
                      ariaLabel="three-dots-loading"
                      wrapperStyle={{ marginRight: '8px' }}
                      visible={true}
                    />
                    Recording...
                  </>
                ) : (
                  'Record Expense'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddExpenseModal;